import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  RefreshCw,
  RotateCcw,
  Save,
  GitPullRequest,
  MessageSquare,
  Link2,
  FileText,
  Info,
  Users,
  UserPlus,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import MembersList from './MembersList';
import InviteMemberDialog from './InviteMemberDialog';
import { RoleBadge } from './RoleSelector';

const ALERT_TYPE_CONFIG = {
  stalled_pr: {
    icon: GitPullRequest,
    title: 'Stalled Pull Requests',
    description: 'Detect PRs that have been open without activity for too long',
    fields: [
      { key: 'stall_threshold_days', label: 'Stall Threshold', unit: 'days', type: 'number', description: 'Days of inactivity before a PR is considered stalled' },
      { key: 'high_threshold_days', label: 'High Severity Threshold', unit: 'days', type: 'number', description: 'Days stalled before severity becomes High' },
      { key: 'critical_threshold_days', label: 'Critical Severity Threshold', unit: 'days', type: 'number', description: 'Days stalled before severity becomes Critical' },
    ],
  },
  high_discussion: {
    icon: MessageSquare,
    title: 'High Discussion Activity',
    description: 'Detect unusual comment/mention activity that may need attention',
    fields: [
      { key: 'comment_threshold', label: 'Comment Threshold', unit: 'comments', type: 'number', description: 'Number of comments in time window to trigger alert' },
      { key: 'mention_threshold', label: 'Mention Threshold', unit: 'mentions', type: 'number', description: 'Number of Slack mentions in time window to trigger alert' },
      { key: 'time_window_hours', label: 'Time Window', unit: 'hours', type: 'number', description: 'Time window for counting activity' },
    ],
  },
  blocked_issue: {
    icon: Link2,
    title: 'Blocked Issues',
    description: 'Detect Linear issues blocked by unresolved dependencies',
    fields: [
      { key: 'high_blocker_count', label: 'High Severity Threshold', unit: 'blockers', type: 'number', description: 'Number of blockers for High severity' },
      { key: 'critical_blocker_count', label: 'Critical Severity Threshold', unit: 'blockers', type: 'number', description: 'Number of blockers for Critical severity' },
    ],
  },
  dependency_change: {
    icon: FileText,
    title: 'Dependency Changes',
    description: 'Detect document changes that may affect code repositories',
    fields: [
      { key: 'importance_threshold', label: 'Importance Threshold', unit: '0-1', type: 'number', step: 0.1, min: 0, max: 1, description: 'Minimum importance score to trigger analysis' },
    ],
  },
};

export default function ProjectSettingsTab({ project, onUpdate }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [localSettings, setLocalSettings] = useState({});

  // Members state
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  const loadMembers = useCallback(async () => {
    if (!project?.id) return;

    setMembersLoading(true);
    try {
      const response = await api.get(`/projects/${project.id}/members`);
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to load members:', error);
      toast.error('Failed to load project members');
    } finally {
      setMembersLoading(false);
    }
  }, [project?.id]);

  const loadSettings = useCallback(async () => {
    if (!project?.id) return;

    try {
      setLoading(true);
      const response = await api.get(`/projects/${project.id}/alert-settings`);
      setSettings(response.data.alert_settings);
      setDefaults(response.data.defaults);

      // Initialize local settings from server
      const initial = {};
      for (const [alertType, data] of Object.entries(response.data.alert_settings)) {
        initial[alertType] = { ...data.settings };
      }
      setLocalSettings(initial);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to load alert settings:', error);
      toast.error('Failed to load alert settings');
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => {
    loadSettings();
    loadMembers();
  }, [loadSettings, loadMembers]);

  const handleChange = (alertType, key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [alertType]: {
        ...prev[alertType],
        [key]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleToggleEnabled = (alertType) => {
    const currentEnabled = localSettings[alertType]?.enabled ?? true;
    handleChange(alertType, 'enabled', !currentEnabled);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build the settings to send (only include changed values)
      const settingsToSend = {};

      for (const [alertType, values] of Object.entries(localSettings)) {
        const defaultValues = defaults[alertType];
        const changedValues = {};

        for (const [key, value] of Object.entries(values)) {
          if (value !== defaultValues[key]) {
            changedValues[key] = value;
          }
        }

        if (Object.keys(changedValues).length > 0) {
          settingsToSend[alertType] = changedValues;
        }
      }

      await api.put(`/projects/${project.id}/alert-settings`, settingsToSend);
      toast.success('Alert settings saved');
      setHasChanges(false);
      loadSettings();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      await api.delete(`/projects/${project.id}/alert-settings`);
      toast.success('Settings reset to defaults');
      loadSettings();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to reset settings:', error);
      toast.error('Failed to reset settings');
    }
  };

  const handleResetField = (alertType, key) => {
    if (defaults && defaults[alertType]) {
      handleChange(alertType, key, defaults[alertType][key]);
    }
  };

  const isFieldCustomized = (alertType, key) => {
    if (!defaults || !localSettings[alertType]) return false;
    return localSettings[alertType][key] !== defaults[alertType][key];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Get current user's role in this project (API returns user_role)
  const currentUserMembership = members.find((m) => m.user_id === user?.id);
  const currentUserRole = project?.user_role || currentUserMembership?.role || 'viewer';
  const canInvite = currentUserRole === 'owner' || currentUserRole === 'admin';

  return (
    <div className="space-y-8">
      {/* Team Members Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Team Members</CardTitle>
                <CardDescription>
                  Manage who has access to this project
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <RoleBadge role={currentUserRole} size="sm" />
              {canInvite && (
                <Button size="sm" onClick={() => setInviteDialogOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <MembersList
            projectId={project?.id}
            members={members}
            currentUserId={user?.id}
            currentUserRole={currentUserRole}
            onMembersChange={loadMembers}
            loading={membersLoading}
          />
        </CardContent>
      </Card>

      {/* Invite Member Dialog */}
      <InviteMemberDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        projectId={project?.id}
        projectName={project?.name}
        currentUserRole={currentUserRole}
        onInviteSent={loadMembers}
      />

      {/* Alert Settings Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Alert Settings</h2>
          <p className="text-sm text-muted-foreground">
            Configure alert thresholds for this project
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset All
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Alert Type Cards */}
      <div className="grid gap-6">
        {Object.entries(ALERT_TYPE_CONFIG).map(([alertType, config]) => {
          const Icon = config.icon;
          const alertSettings = localSettings[alertType] || {};
          const isEnabled = alertSettings.enabled !== false;
          const serverData = settings?.[alertType];
          const isCustomized = serverData?.is_custom || false;

          return (
            <Card key={alertType} className={!isEnabled ? 'opacity-60' : ''}>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        {config.title}
                        {isCustomized && (
                          <Badge variant="outline" className="text-xs">
                            Customized
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {config.description}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`${alertType}-enabled`} className="text-sm text-muted-foreground">
                      {isEnabled ? 'Enabled' : 'Disabled'}
                    </Label>
                    <Switch
                      id={`${alertType}-enabled`}
                      checked={isEnabled}
                      onCheckedChange={() => handleToggleEnabled(alertType)}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {config.fields.map((field) => {
                    const value = alertSettings[field.key] ?? defaults?.[alertType]?.[field.key] ?? '';
                    const defaultValue = defaults?.[alertType]?.[field.key];
                    const isCustom = isFieldCustomized(alertType, field.key);

                    return (
                      <div key={field.key} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <Label htmlFor={`${alertType}-${field.key}`} className="text-sm">
                              {field.label}
                            </Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">{field.description}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          {isCustom && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => handleResetField(alertType, field.key)}
                            >
                              Reset
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            id={`${alertType}-${field.key}`}
                            type={field.type}
                            value={value}
                            onChange={(e) => {
                              const newValue = field.type === 'number'
                                ? parseFloat(e.target.value) || 0
                                : e.target.value;
                              handleChange(alertType, field.key, newValue);
                            }}
                            step={field.step}
                            min={field.min}
                            max={field.max}
                            disabled={!isEnabled}
                            className={isCustom ? 'border-primary' : ''}
                          />
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            {field.unit}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Default: {defaultValue} {field.unit}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="fixed bottom-4 right-4 bg-background border rounded-lg shadow-lg p-4 flex items-center gap-4">
          <span className="text-sm">You have unsaved changes</span>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      )}
    </div>
  );
}
